import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../appRedux/store";
import { ParkingRequestTypeConstant, ParkingRequestTypeIdConstant } from "../../constants/DatabaseConstants/ParkingConstants";
import * as parkingServices from "../../services/parkingServices";
import useValue from "../../hooks/useValue";
import { CustomNotification } from "../../util/CustomNotification";
import { DefaultPagination } from "../../constants/generalConstants";
import useTablePagination from "../../hooks/useTablePagination";

import type { GetScheduleList, ParamsObject } from "../../shared/interface/parking.interface";
import type FixMeLater from "../../shared/types/fixMeLater.type";

const useScheduleList = () => {
  /* CONSTANTS */

  /* STATE */
  const [searchedText, setSearchedText] = useState<string>("");
  const [mode, setMode] = useState<string>(ParkingRequestTypeConstant.RETRIEVAL);
  const [scheduleList, setScheduleList] = useState<GetScheduleList[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [recordID, setRecordID] = useState<FixMeLater | null>(null);

  const { masterFilters } = useSelector(({ common }: RootState) => common);

  const { value: loading, handleEnableValue: handleEnableLoading, handleDisableValue: handleDisableLoading } = useValue(false);
  const { value: saving, handleEnableValue: handleEnableSaving, handleDisableValue: handleDisableSaving } = useValue(false);
  const { value: refetch, handleEnableValue: handleEnableRefetch, handleDisableValue: handleDisableRefetch } = useValue(false);

  const { pagination, handleChangePagination, handleChangeTotalSize, handleResetPagination } = useTablePagination(0);

  /* HANDLERS */
  let startIndex = ((pagination?.current ?? 1) - 1) * (pagination?.pageSize ?? 6);

  const handleModeChange = (data: string) => {
    handleChangeTotalSize(scheduleList?.length);
    setMode(data);
    handleResetPagination();
  };

  const handleChangeOnSearch = (value: string) => {
    handleChangeTotalSize(scheduleList?.length);
    const trimmedValue = value?.replace(/^\s+/, "");
    setSearchedText(trimmedValue);
  };

  const handleChangeSearch = (value: string) => {
    handleChangeTotalSize(scheduleList?.length);
    const trimmedValue = value?.replace(/^\s+/, "");
    setSearchedText(trimmedValue);
    if (!trimmedValue) handleEnableRefetch();
  };

  const handleCancel = (record: FixMeLater) => {
    setRecordID(record);
    setIsModalOpen(true);
  };

  const handleOk = async () => {
    try {
      if (!recordID?.id) return;
      handleEnableSaving();

      const res = await parkingServices.CancelScheduledList(recordID?.id);
      CustomNotification.showSuccessMessage(res?.message);
      handleEnableRefetch();
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      setIsModalOpen(false);
      setRecordID(null);
      handleDisableSaving();
    }
  };

  const handleCancelModal = () => {
    setIsModalOpen(false);
    setRecordID(null);
  };

  /* EFFECT */
  useEffect(() => {
    handleEnableRefetch();

    /* eslint-disable-next-line */
  }, [mode, searchedText, pagination?.current, pagination?.pageSize, masterFilters?.customerId, masterFilters?.locationId]);

  useEffect(() => {
    const fetchScheduleList = async () => {
      try {
        handleEnableLoading();
        const requestId = mode === ParkingRequestTypeConstant.BOOKING ? ParkingRequestTypeIdConstant.BOOKING : ParkingRequestTypeIdConstant.RETRIEVAL;

        const paramsObject: ParamsObject = {
          requestId,
          searchQuery: searchedText,
          page: pagination?.current || DefaultPagination.PAGE,
          pageSize: pagination?.pageSize || DefaultPagination.PAGE_SIZE,
          customerId: masterFilters?.customerId,
          locationId: masterFilters?.locationId
        };

        const response = await parkingServices.getScheduleListByRequestId(paramsObject);

        const formattedResponse: FixMeLater = response?.data?.rows?.map((item, index) => {
          return {
            ...item,
            sr_no: startIndex + index + 1,
            user_name: `${item?.keyFobAllocation?.mobileUser?.first_name ?? ""} ${item?.keyFobAllocation?.mobileUser?.last_name ?? ""}`,
            key_FOB: `${item?.keyFob?.key_fob_token} - ${item?.keyFob?.vehicleType?.vehicle_type_name}`,
            location: item?.customerLocation?.location_name ?? "",
            client: item?.customerClient?.client_name ?? "",
            key_fob: item?.keyFob?.key_fob_token ?? "",
            key_fob_display_value: `${item?.keyFob?.key_fob_token} - ${item?.keyFob?.vehicleType?.vehicle_type_name}`,
            status: item?.is_confirmed ? "Confirmed" : ""
          };
        });

        setScheduleList(formattedResponse);
        handleChangeTotalSize(response?.data?.count || 0);
      } catch (error) {
        CustomNotification.showErrorMessage(error);
      } finally {
        handleDisableRefetch();
        handleDisableLoading();
      }
    };

    if (refetch) fetchScheduleList();

    /* eslint-disable-next-line */
  }, [refetch]);

  return {
    loading,
    scheduleList,
    handleModeChange,
    mode,
    pagination,
    handleChangePagination,
    handleChangeOnSearch,
    handleChangeSearch,
    handleCancel,
    isModalOpen,
    recordID,
    handleOk,
    searchedText,
    handleCancelModal,
    saving
  };
};

export default useScheduleList;
